import { useContext, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import TagManager from 'react-gtm-module'
import { selectToken } from 'containers/authentication/authenticationSlice'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useConfirmation from 'containers/confirmation/useConfirmation'
import api from 'containers/api'
import { completeShopifyStoreSwitch, switchDoneKey } from './shopifyStoreSwitch'

/**
 * Handles the pending Shopify store switch signalled by `?shopify_switch=…` on the Sources URL
 * (set by the OAuth-connect redirect when a DIFFERENT store is connected to an org that already
 * has one).
 *
 * Integrity: the confirm modal may be opened ONLY by that redirect — never by an unrelated
 * re-render (org switch, remount, token refresh). The one-shot params are consumed synchronously
 * (once-per-mount ref + URL strip) before anything async, and a completed switch is recorded in
 * sessionStorage so a remount / back-button to a stale URL can't re-run it. A cancelled/failed
 * switch is intentionally NOT recorded, so reconnecting the store re-offers it.
 */
export default function useShopifyStoreSwitch(refreshAccounts: (orgId: number) => void) {
  const history = useHistory()
  const location = useLocation()
  const { currentOrganization } = useContext(OrganizationsContext)
  const token = useSelector(selectToken)
  const { confirm } = useConfirmation()
  const handledRef = useRef(false)
  const unmountedRef = useRef(false)

  // Track TRUE unmount separately from dependency changes. The switch effect strips its own
  // `?shopify_switch=…` trigger via history.replace() below, which mutates location.search and
  // re-runs the effect — so a per-run `cancelled` cleanup flag would flip mid-switch (before the
  // awaited confirm() even resolves) and silently suppress the post-switch redirect/refresh,
  // stranding the user with both stores deactivated. This ref only flips on real unmount.
  useEffect(() => () => { unmountedRef.current = true }, [])

  useEffect(() => {
    if (handledRef.current) return
    const params = new URLSearchParams(location.search)
    if (params.get('shopify_switch') !== 'true') return
    if (!currentOrganization.id || !token) return
    const toAccount = params.get('to_account')
    const fromShop = params.get('from_shop') || ''
    const toShop = params.get('to_shop') || ''
    if (!toAccount) return

    // Skip only switches already SUCCESSFULLY completed (persisted after the fact).
    try {
      if (sessionStorage.getItem(switchDoneKey(toAccount))) return
    } catch (e) { /* sessionStorage unavailable — rely on the in-mount ref + URL strip */ }

    // In-mount guard + synchronous URL strip: an org switch / re-render now finds no params and
    // bails. (Resets on remount, which is fine — only a *succeeded* switch is suppressed, via
    // sessionStorage; a cancelled/failed one should be re-offered on reconnect.)
    // The strip MUST stay on the current route (drop only the search): navigating to a
    // different path — e.g. the legacy /sources, which is now a <Redirect> — would unmount
    // this hook mid-switch, flipping unmountedRef and silently suppressing the post-switch
    // redirect/refresh/onError.
    handledRef.current = true
    const slug = currentOrganization.slug
    const orgId = currentOrganization.id
    history.replace(location.pathname)

    completeShopifyStoreSwitch(
      { toAccount, fromShop, toShop },
      {
        slug,
        orgId,
        token,
        confirm,
        switchStore: api.switchShopifyStore,
        redirect: (path) => { if (!unmountedRef.current) history.replace(path) },
        refreshAccounts: (id) => { if (!unmountedRef.current) refreshAccounts(id) },
        // No toast system in this app — surface the failure through the same modal the switch
        // flow already uses, so a billing-critical failure never closes silently.
        onError: (message) => { if (!unmountedRef.current) confirm(message) },
        storage: window.sessionStorage,
        track: (event) => TagManager.dataLayer(event),
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, currentOrganization.id, token])
}
